import React from 'react'
import DashboardLayout from '../../components/Admin/DashboardLayout'
import { MDBContainer, MDBRow, MDBCol, MDBIcon, MDBBtn } from 'mdb-react-ui-kit'
import { toast } from 'react-toastify'
import UserProfileService from './../../shared/services/UserProfile.service'
import UserAlertService from './../../shared/services/UserAlert.service'
import { UsersCarrierContractFactory } from '../../shared/factories'
import ACMStore from '../../components/contracting/AgentContractManager.store'
import CarrierStore from '../../components/contracting/Carrier.store'
import AgentContractEditor from '../../components/contracting/AgentContractEditor/AgentContractEditor.component'

import './AccountAlerts.scss'
import UserAlertsStore from '../../components/users/UserAlertsIndicator/UserAlerts.store'
import PushSettingsPanel from '../../components/push/PushSettingsPanel'
import PushMessageService from '../../shared/services/PushMessage.service'
import CarrierService from '../../shared/services/Carrier.service'

const ALERT_KEYS = {
  'contract.requires-agent-sig': {
    icon: 'exclamation-circle',
    classes: ['alert-danger'],
    show_actions: false,
    auto_acknowledge: UserAlert => {
      if (UserAlert && !UserAlert?.acknowledged_at && UserProfileService.get('has_onboarded')) return true
      return false
    },
  },
  'contract.unfinished-cart-packet': {
    icon: 'exclamation-circle',
    classes: ['alert-danger'],
    show_actions: true,
    auto_acknowledge: UserAlert => {
      if (UserAlert && !UserAlert?.acknowledged_at && UserProfileService.get('has_onboarded')) return true
      return false
    },
  },
  'contract.requires-writing-code': {
    icon: 'exclamation-circle',
    classes: ['alert-danger'],
    show_actions: true,
    auto_acknowledge: UserAlert => {
      if (UserAlert && !UserAlert?.acknowledged_at && UserProfileService.get('has_onboarded')) return true
      return false
    },
  },
  'user.training-stale': {
    icon: 'exclamation-circle',
    classes: ['alert-danger'],
    show_actions: true,
    auto_acknowledge: UserAlert => {
      if (UserAlert && !UserAlert?.acknowledged_at && UserProfileService.get('has_onboarded')) return true
      return false
    },
  },
  'user.usertype-upgrade': {
    icon: false,
    classes: ['alert-warning'],
    show_actions: false,
    auto_acknowledge: true,
  },
  'user.usertype-downgrade': {
    icon: false,
    classes: ['alert-warning'],
    show_actions: false,
    auto_acknowledge: true,
  },
  'user.missing-photo': {
    icon: false,
    classes: ['alert-warning'],
    show_actions: true,
    auto_acknowledge: true,
  },
  'billing.failed-payment.sig-agent': {
    icon: 'exclamation-triangle',
    classes: ['alert-danger'],
    show_actions: true,
    auto_acknowledge: true,
  },
  'billing.failed-payment.10x-crm': {
    icon: 'exclamation-triangle',
    classes: ['alert-danger'],
    show_actions: true,
    auto_acknowledge: true,
  },
  'billing.subscription-canceled.sig-agent': {
    icon: 'exclamation-triangle',
    classes: ['alert-danger'],
    show_actions: true,
    auto_acknowledge: true,
  },
  'billing.subscription-canceled.10x-crm': {
    icon: 'exclamation-triangle',
    classes: ['alert-danger'],
    show_actions: true,
    auto_acknowledge: true,
  },
  'billing.subscription-canceled.admin-email': {
    icon: 'exclamation-triangle',
    classes: ['alert-danger'],
    show_actions: true,
    auto_acknowledge: true,
  },
  'billing.subscription-canceled.dyl-voip': {
    icon: 'exclamation-triangle',
    classes: ['alert-danger'],
    show_actions: true,
    auto_acknowledge: true,
  },
  // A direct message from the Broker Support Team.
  //
  // `alert-info`, NOT `alert-danger`: red is already spent on contract and
  // billing failures — things that are the agent's problem to fix. A message
  // from the office is a notice. The bullhorn carries the meaning, and
  // severity='critical' earns its weight through behaviour (the notification
  // stays on screen, and a banner persists until acknowledged) rather than
  // through colour. If everything is red, nothing is.
  //
  // auto_acknowledge is false on purpose: acknowledgement is the only signal
  // we have that the agent actually saw it.
  'admin.push-notification': {
    icon: 'bullhorn',
    classes: ['alert-info'],
    show_actions: true,
    auto_acknowledge: false,
  },
}

const getDefinition = alert => {
  try {
    return ALERT_KEYS[alert.alert_key]
  } catch (ex) {
    return false
  }
}

class AccountAlert extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      tabName: 'active',
      isLoading: true,
      alerts: [],
      // push_messages.id -> row, for the alerts that carry a link.
      pushMessages: {},
    }
  }

  componentDidMount() {
    this.fetchAccountAlertsData()
  }

  /**
   * `user_alerts` has no link column, so the optional deep link for a BST
   * message lives on `push_messages`. Fetched once for the whole page and
   * mapped by id rather than per row.
   */
  fetchPushMessages = async () => {
    const alerts = this.state.alerts || []
    if (!alerts.some(a => a.alert_key === 'admin.push-notification')) return

    try {
      const result = await PushMessageService.search({
        search: { user_id: UserProfileService.getUserId() },
        pagination: false,
      })
      const models = (result && result.models) || (Array.isArray(result) ? result : [])
      const pushMessages = {}
      models.forEach(m => {
        pushMessages[`${m.id}`] = m
      })
      this.setState({ pushMessages })
    } catch (ex) {
      // Non-fatal: the alert still renders, just without its "Open" button.
      console.error('Failed to load push messages for alerts page.', ex)
    }
  }

  fetchAccountAlertsData = async () => {
    if (!this.isLoading) this.setState({ isLoading: true })
    this.setState(
      {
        alerts: await UserProfileService.fetchAlerts(),
        isLoading: false,
      },
      () => {
        if (Array.isArray(this.state.alerts)) {
          UserAlertsStore.setNumberOfAlerts(this.state.alerts.length)
          this.fetchPushMessages()
          this.state.alerts.forEach(async alert => {
            let auto_acknowledgeable = false
            // Guarded: an alert_key with no ALERT_KEYS entry used to throw here.
            // Inside an async forEach callback that surfaces as an unhandled
            // rejection and the alert is silently skipped, so a key added on the
            // backend without a matching entry here would fail invisibly.
            const definition = getDefinition(alert) || {}
            if (typeof definition.auto_acknowledge === 'function') {
              auto_acknowledgeable = definition.auto_acknowledge(alert)
            } else {
              auto_acknowledgeable = definition.auto_acknowledge
            }
            if (auto_acknowledgeable) {
              await UserAlertService.acknowledged(alert.id)
              if (UserAlertsStore.numberOfAlerts > 0)
                UserAlertsStore.setNumberOfAlerts(UserAlertsStore.numberOfAlerts - 1)
            }
          })
          let alertIds = this.state.alerts.filter(alert => !alert.seen_at).map(alert => alert.id)
          if (alertIds.length > 0 && !UserProfileService.isAssumed())
            alertIds.forEach(alertId => UserAlertService.seen(alertId))
        }
      }
    )
  }

  isActive = tabName => this.state.tabName === tabName

  change = tabName => {
    if (!this.isActive(tabName)) this.setState({ tabName })
  }

  openContractEditModal = async alert => {
    const contract = (
      (await UsersCarrierContractFactory.search({
        search: { id: parseInt(alert?.reference_id) },
        pagination: false,
      })) || []
    ).shift()

    const carrier = await CarrierService.get(contract?.get('carrier_id'))

    ACMStore.Contract = contract
    ACMStore.isEditContractAlertPage = true
    CarrierStore._examineCarrier = carrier
    CarrierStore.examineAgentContract(contract)
  }

  acknowledgeAlert = alert =>
    alert?.id
      ? UserAlertService.acknowledged(alert.id).then(res => {
          if (res && res?.id)
            this.setState(prevState => ({
              ...prevState,
              alerts: prevState.alerts.map(a => {
                if (parseInt(a.id) === parseInt(res.id)) a.acknowledged_at = res.acknowledged_at
                return a
              }),
            }))
        })
      : null

  renderActions = alert => {
    switch (alert && alert?.alert_key) {
      case 'admin.push-notification': {
        const message = this.state.pushMessages[`${alert.reference_id}`]
        const link = message && message.link_url
        return (
          <>
            {link ? (
              <MDBBtn tag='a' href={link}>
                Open
              </MDBBtn>
            ) : null}
            <MDBBtn onClick={() => this.acknowledgeAlert(alert)}>Mark As Read</MDBBtn>
          </>
        )
      }
      case 'user.missing-photo':
        return (
          <MDBBtn type='button' color='default' href='/account-profile'>
            Upload Now
          </MDBBtn>
        )
      case 'billing.failed-payment.sig-agent':
        return (
          <MDBBtn type='button' color='default' href='/account-billinginfo'>
            Update Billing
          </MDBBtn>
        )
      case 'billing.subscription-canceled.10x-crm':
      case 'billing.subscription-canceled.admin-email':
      case 'billing.subscription-canceled.dyl-voip':
        return (
          <MDBBtn
            type='button'
            color='default'
            href='https://usabg.atlassian.net/servicedesk/customer/portal/2/group/8/create/9'
            tag='a'
            target='_BLANK'
          >
            Submit Broker Support Ticket
          </MDBBtn>
        )
      case 'billing.subscription-canceled.sig-agent':
        return (
          <MDBBtn
            type='button'
            color='default'
            onClick={() =>
              alert?.id
                ? UserAlertService.acknowledged(alert.id).then(res => {
                    if (res && res?.id)
                      this.setState(prevState => ({
                        ...prevState,
                        alerts: prevState.alerts.map(alert => {
                          if (parseInt(alert.id) === parseInt(res.id)) alert.acknowledged_at = res.acknowledged_at
                          return alert
                        }),
                      }))
                  })
                : null
            }
          >
            Acknowledge
          </MDBBtn>
        )
      case 'billing.failed-payment.10x-crm':
        return (
          <MDBBtn type='button' color='default' tag='a' href='https://10x.usabg.com/' target='_BLANK'>
            Update 10x Billing
          </MDBBtn>
        )
      case 'contract.unfinished-cart-packet':
        return (
          <MDBBtn type='button' color='default' tag='a' href='/welcome'>
            Complete Onboard Contracting
          </MDBBtn>
        )
      case 'user.training-stale':
        return (
          <MDBBtn type='button' color='default' tag='a' href='/trainings'>
            Complete Trainings
          </MDBBtn>
        )
      case 'contract.requires-writing-code':
        return (
          <MDBBtn type='button' color='default' tag='a' onClick={e => this.openContractEditModal(alert)}>
            Input Writing Number
          </MDBBtn>
        )
      default:
        return <></>
    }
  }

  renderAlertRow = alert => {
    let definition = getDefinition(alert) || {}

    const getKeyGroup = alertKey =>
      alertKey ? alertKey.split('.')[0].charAt(0).toUpperCase() + alertKey.split('.')[0].substr(1) : ''

    // user_alerts stores only the body; the title lives on push_messages.
    const pushTitle =
      alert.alert_key === 'admin.push-notification' && this.state.pushMessages[`${alert.reference_id}`]?.title

    return (
      <li
        key={'alert-id-' + alert.id}
        className={(Array.isArray(definition?.classes) ? definition.classes : [])
          .concat([!!definition?.show_actions && !alert.acknowledged_at ? 'show-actions' : null])
          .filter(c => !!c)
          .join(' ')}
      >
        <div className='alert-key'>
          {definition?.icon ? <MDBIcon icon={definition.icon}></MDBIcon> : <></>}
          {getKeyGroup(alert.alert_key)}
        </div>
        <div className='alert-message'>
          {pushTitle ? <strong className='d-block'>{pushTitle}</strong> : null}
          {alert.alert_message}
        </div>
        <div className='alert-action'>{this.renderActions(alert)}</div>
      </li>
    )
  }

  renderTab = tabName => {
    let alerts = this.state.alerts.filter(
      alert => (tabName === 'active' && !alert.acknowledged_at) || (tabName === 'history' && alert.acknowledged_at)
    )

    return (
      <div className={'tab-pane fade ' + (this.isActive(tabName) ? 'show active' : '')} role='tabpanel'>
        {alerts.length ? <ul>{alerts.map(this.renderAlertRow)}</ul> : <span>Your alerts are all caught up!</span>}
      </div>
    )
  }

  /**
   * Push-notification controls, as a tab rather than a permanent block above
   * the alert list.
   *
   * Rendered unconditionally like the other panes — visibility is CSS-driven,
   * so the panel's device fetch still runs on page load rather than waiting
   * for the agent to open the tab.
   */
  renderSettingsTab = () => (
    <div
      className={'tab-pane settings-pane fade ' + (this.isActive('settings') ? 'show active' : '')}
      role='tabpanel'
      aria-labelledby='settings-tab-md'
    >
      <PushSettingsPanel />
    </div>
  )

  render() {
    toast.configure()

    return (
      <React.Fragment>
        <DashboardLayout>
          <main id='AccountAlertsPage' className='mainSection mt-5 pb-5'>
            <MDBContainer fluid>
              <MDBRow>
                <MDBCol size='12'>
                  <h2>Account Alerts</h2>
                  <hr />
                </MDBCol>
              </MDBRow>
              <MDBRow>
                <MDBCol size='12'>
                  <section className='mx-2 p-4'>
                    <ul className='nav nav-tabs md-tabs' id='myTabMD' role='tablist'>
                      <li className='nav-item waves-effect waves-light'>
                        <a
                          className={'nav-link ' + (this.isActive('active') ? 'active' : '')}
                          id='current-tab-md'
                          data-toggle='tab'
                          href='#current-md'
                          role='tab'
                          aria-controls='home-md'
                          aria-selected={this.isActive('active')}
                          onClick={() => this.change('active')}
                        >
                          Current
                        </a>
                      </li>
                      <li className='nav-item waves-effect waves-light'>
                        <a
                          className={'nav-link ' + (this.isActive('history') ? 'active' : '')}
                          id='history-tab-md'
                          data-toggle='tab'
                          href='#history-md'
                          role='tab'
                          aria-controls='profile-md'
                          aria-selected={this.isActive('history')}
                          onClick={() => this.change('history')}
                        >
                          Past
                        </a>
                      </li>
                      {/* ml-auto pushes this to the right edge of the flex
                          strip: settings are not a third category of alert, so
                          they should not read as the next item in the sequence. */}
                      <li className='nav-item waves-effect waves-light ml-auto'>
                        <a
                          className={'nav-link ' + (this.isActive('settings') ? 'active' : '')}
                          id='settings-tab-md'
                          data-toggle='tab'
                          href='#settings-md'
                          role='tab'
                          aria-controls='settings-md'
                          aria-selected={this.isActive('settings')}
                          onClick={() => this.change('settings')}
                        >
                          <MDBIcon icon='cog' /> Settings
                        </a>
                      </li>
                    </ul>
                    <div className='tab-content card pt-5' id='mymdbTabContentMD'>
                      {this.renderTab('active')}
                      {this.renderTab('history')}
                      {this.renderSettingsTab()}
                    </div>
                  </section>
                </MDBCol>
              </MDBRow>
              <AgentContractEditor />
            </MDBContainer>
          </main>
        </DashboardLayout>
      </React.Fragment>
    )
  }
}

export default AccountAlert
